import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../utils/api';
import { PlansContext } from '../App';
import { useAuth } from '../context/AuthContext';
import TimeSelector from '../components/TimeSelector/TimeSelector';
import styles from '../styles/Record.module.css';
import { useDocumentHead } from '../hooks/useDocumentHead';
import CustomAudioPlayer from '../components/CustomAudioPlayer/CustomAudioPlayer';
import CustomVideoPlayer from '../components/CustomVideoPlayer/CustomVideoPlayer';
import NonLiveTranscription from '../components/NonLiveTranscription/NonLiveTranscription';
import DeviceSettingsPanel from '../components/DeviceSettingsPanel/DeviceSettingsPanel';
import { FileText } from 'lucide-react';
import VideoAnalyzer from '../components/VideoAnalyzer/VideoAnalyzer';
import VideoAnalysisLogger from '../components/VideoAnalysisLogger/VideoAnalysisLogger';

const Record = ({ isRehearsal = false, originalPresentationId = null }) => {
  useDocumentHead(
    'Record Your Speech | TopSpeaks',
    'Record and get real-time feedback on your speeches with AI-powered analysis.',
    'index, follow'
  );

  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [transcriptData, setTranscriptData] = useState({
    fullText: '',
    segments: [],
    audioBlob: null
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  // eslint-disable-next-line
  const [title, setTitle] = useState('');
  // eslint-disable-next-line 
  const [isSaving, setIsSaving] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isTimeLimitReached, setIsTimeLimitReached] = useState(false);
  const [videoStream, setVideoStream] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [useVideo, setUseVideo] = useState(false);
  const [transcriptionMode] = useState('standard');
  const [processingState, setProcessingState] = useState('idle');
  const [isProcessing, setIsProcessing] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedDuration, setSelectedDuration] = useState(0);
  const [localPlanLimits, setLocalPlanLimits] = useState(null);
  const { user, planLimits: contextPlanLimits, handleAuthSuccess } = useAuth();
  const [timeLimit, setTimeLimit] = useState(60);
  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const navigate = useNavigate();
  const { handleLimitReached } = useContext(PlansContext);
  const [audioBlob, setAudioBlob] = useState(null);
  const audioChunksRef = useRef([]);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);

  const selectedAudioDeviceRef = useRef(null);
  const selectedVideoDeviceRef = useRef(null);
  const videoAnalysisLogger = useRef(VideoAnalysisLogger()).current;

  const [selectedAudioDevice, setSelectedAudioDevice] = useState(null);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
  // eslint-disable-next-line
  const [videoAnalysis, setVideoAnalysis] = useState({
    faceExpressions: [],
    poseData: [],
    handGestures: []
  });

  useEffect(() => {
    if (contextPlanLimits && user?.plan?.limits?.suggested_duration) {
      const suggestedMinutes = Math.floor(user.plan.limits.suggested_duration / 60);

      // Calculate total available minutes, including purchased minutes
      const calculateTotalMinutes = (limitData) => {
        const baseMinutes = limitData.limit - limitData.used;
        const purchasedMinutes = limitData.purchased.minutes;
        return baseMinutes + purchasedMinutes;
      };

      const remainingMinutes = calculateTotalMinutes(contextPlanLimits.limits.non_live_minutes);

      const initialMinutes = Math.min(suggestedMinutes, remainingMinutes);

      // Only update if not already set by user
      if (selectedDuration === 0) {
        setSelectedDuration(initialMinutes);
        setTimeLimit(initialMinutes * 60);
      }
    }
  }, [contextPlanLimits, user, transcriptionMode, selectedDuration]);

  useEffect(() => {
    if (contextPlanLimits && !localPlanLimits) {
      updateLocalPlanLimits()
    }
    // eslint-disable-next-line 
  }, [contextPlanLimits]);

  useEffect(() => {
    if (localPlanLimits) {
      const remainingMinutes = getRemainingMinutes();
      const suggestedMinutes = Math.floor(user?.plan?.limits?.suggested_duration / 60) || 0;
      const newDuration = Math.min(suggestedMinutes, remainingMinutes);
      setSelectedDuration(newDuration);
    }
    // eslint-disable-next-line 
  }, [localPlanLimits, user?.plan?.limits?.suggested_duration]);

  // Clean up video stream on unmount
  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= timeLimit) {
            setIsTimeLimitReached(true);
            stopRecording();
            return timeLimit;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
    // eslint-disable-next-line
  }, [isRecording, timeLimit]);

  const handleLanguageChange = (language) => {
    if (!isRecording) {
      setSelectedLanguage(language);
    }
  };

  const handleCameraToggle = (isEnabled) => {
    setUseVideo(isEnabled);
  };

  const handleAudioDeviceChange = (deviceId) => {
    setSelectedAudioDevice(deviceId);
    selectedAudioDeviceRef.current = deviceId;

    if (mediaRecorderRef.current && !isRecording) {
      navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } }
      }).then(stream => {
        // If in video mode, keep existing video track
        const newStream = useVideo && mediaRecorderRef.current.stream
          ? new MediaStream([stream.getAudioTracks()[0], mediaRecorderRef.current.stream.getVideoTracks()[0]])
          : stream;

        // Create new MediaRecorder
        const newRecorder = new MediaRecorder(newStream);

        // Copy over event handlers
        if (mediaRecorderRef.current.ondataavailable) {
          newRecorder.ondataavailable = mediaRecorderRef.current.ondataavailable;
        }
        if (mediaRecorderRef.current.onstop) {
          newRecorder.onstop = mediaRecorderRef.current.onstop;
        }

        mediaRecorderRef.current = newRecorder;
      }).catch(error => {
        console.error('Error switching audio device:', error);
      });
    }
  };

  const handleVideoDeviceChange = (deviceId) => {
    setSelectedVideoDevice(deviceId);
    selectedVideoDeviceRef.current = deviceId;

    if (mediaRecorderRef.current && !isRecording && useVideo) {
      navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } }
      }).then(stream => {
        const existingAudioTrack = mediaRecorderRef.current.stream.getAudioTracks()[0];
        const newStream = new MediaStream([stream.getVideoTracks()[0], existingAudioTrack]);
        mediaRecorderRef.current.stream = newStream;
      }).catch(error => {
        console.error('Error switching video device:', error);
      });
    }
  };

  const checkPresentationLimit = async () => {
    try {
      setIsStarting(true);
      // eslint-disable-next-line
      const response = await authenticatedFetch('/api/presentation/start', {
        method: 'POST',
        body: {
          mode: transcriptionMode,
          requested_minutes: selectedDuration
        }
      });
      setTimeLimit(selectedDuration * 60);
      return true;
    } catch (error) {
      console.error('Error checking presentation limit:', error);
      if (error.limitReached) {
        handleLimitReached();
        return false;
      }
      alert('Failed to start presentation. Please try modifying minutes in Presentation Duration.');
      return false;
    } finally {
      setIsStarting(false);
    }
  };

  const getMimeType = () => {
    if (useVideo) {
      if (MediaRecorder.isTypeSupported('video/mp4')) {
        return 'video/mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        return 'video/webm';
      }
    } else {
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        return 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        return 'audio/webm;codecs=opus';
      }
    }
    // Fallback
    return useVideo ? 'video/webm' : 'audio/webm;codecs=opus';
  };

  const startRecording = async () => {
    if (!selectedAudioDevice) {
      setShowPermissionPrompt(true);
      return;
    }
    if (!user.plan.features.advance_analytics && !selectedVideoDevice) {
      setShowPermissionPrompt(true);
      return;
    }

    try {
      const canStart = await checkPresentationLimit();
      if (!canStart) return;

      const constraints = {
        audio: {
          deviceId: selectedAudioDeviceRef.current ? { exact: selectedAudioDeviceRef.current } : undefined,
          sampleRate: 44100,
          channelCount: 2,
          echoCancellation: true,
          noiseSuppression: true
        },
        video: useVideo ? {
          deviceId: selectedVideoDeviceRef.current ? { exact: selectedVideoDeviceRef.current } : undefined,
          width: { ideal: 1280, max: 1280 },
          height: { ideal: 720, max: 720 },
          facingMode: "user",
          frameRate: { max: 30 }
        } : false
      };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (useVideo) {
          setVideoStream(stream);
          setShowPreview(true);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }

          // Try to enter fullscreen mode after getting user media permissions
          const videoAnalyzer = document.querySelector('[data-component="video-analyzer"]');
          if (videoAnalyzer) {
            try {
              await videoAnalyzer.requestFullscreen();
            } catch (error) {
              console.warn('Fullscreen request failed:', error);
              // Continue with recording even if fullscreen fails
            }
          }
        }

        const mimeType = getMimeType();
        mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });

        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          const recordedBlob = new Blob(audioChunksRef.current, { type: mimeType });
          setAudioBlob(recordedBlob);
        };

        mediaRecorderRef.current.start(250);

        setIsRecording(true);
        setIsTimeLimitReached(false);
        setRecordingTime(0);
        setTranscriptData({ fullText: '', segments: [] });
        setAudioBlob(null);

      } catch (err) {
        console.error('MediaDevices error:', err.name, err.message);
        setShowPermissionPrompt(true);
      }
    } catch (err) {
      console.error('Error in startRecording:', err);
      alert('Failed to start recording. Please try again.');
    }
    if (useVideo) {
      videoAnalysisLogger.resetLogger();
      videoAnalysisLogger.startLogging();
    }
  };

  const updateLocalPlanLimits = useCallback(async () => {
    setIsLoadingLimits(true);
    try {
      const data = await authenticatedFetch('/api/auth/limits');
      setLocalPlanLimits(data);

      // Helper function to calculate total available minutes
      const calculateTotalMinutes = (limitData) => {
        const baseMinutes = limitData.limit - limitData.used;
        const purchasedMinutes = limitData.purchased.minutes;
        return baseMinutes + purchasedMinutes;
      };

      // Calculate remaining minutes including purchased minutes
      const remainingMinutes = calculateTotalMinutes(data.limits.non_live_minutes);

      const suggestedMinutes = Math.floor(user?.plan?.limits?.suggested_duration / 60) || 0;
      const newDuration = Math.min(suggestedMinutes, remainingMinutes);

      // Update both selectedDuration and timeLimit
      setSelectedDuration(newDuration);
      setTimeLimit(newDuration * 60);

      return data;
    } catch (error) {
      console.error('Error updating plan limits:', error);
      return null;
    } finally {
      setIsLoadingLimits(false);
    }
  }, [user?.plan?.limits?.suggested_duration]);

  useEffect(() => {
    // setTimeLimit(selectedDuration * 60);
  }, [selectedDuration]);

  const stopRecording = useCallback(async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
        setVideoStream(null);
        setShowPreview(false);
      }

      setIsRecording(false);
      setProcessingState('stopped');

      // Handle non-live processing
      const recordedBlob = new Blob(audioChunksRef.current, { type: getMimeType() });
      setAudioBlob(recordedBlob);

      // Check if user is on Student plan
      if (user?.plan?.features?.advance_analytics === false) {
        // Skip transcript processing for Student plan
        // Just set empty transcript data
        setTranscriptData({ fullText: 'Transcript available with professional plan.', segments: [] });
      } else {
        // Process transcript for other plans
        await handleNonLiveProcessing(recordedBlob);
      }

      // Update plan limits
      const updatedLimits = await updateLocalPlanLimits();
      if (updatedLimits) {
        const remainingMinutes = updatedLimits.limits.non_live_minutes.limit - updatedLimits.limits.non_live_minutes.used;

        const suggestedMinutes = Math.floor(user?.plan?.limits?.suggested_duration / 60) || 0;
        const newDuration = Math.min(suggestedMinutes, remainingMinutes);
        setSelectedDuration(newDuration);
      }

      let videoAnalysisData = null;
      if (isVideoRecording.current) {
        videoAnalysisData = videoAnalysisLogger.stopLogging();
      }

      if (videoAnalysisData) {
        setVideoAnalysis(videoAnalysisData);
      }
    }
    // eslint-disable-next-line 
  }, [isRecording, videoStream, updateLocalPlanLimits, user?.plan?.limits?.suggested_duration, user?.plan?.features?.advance_analytics]);

  const handleTimeSelected = (minutes) => {
    setSelectedDuration(minutes);
    setTimeLimit(minutes * 60);
  };

  const handleTranscriptUpdate = (newTranscriptData) => {
    setTranscriptData(newTranscriptData);
    if (newTranscriptData.audioBlob) {
      setAudioBlob(newTranscriptData.audioBlob);
    }
  };

  const handleNonLiveProcessing = async (blob) => {
    setIsProcessing(true);
    try {
      const nonLiveComponent = document.querySelector('[data-component="non-live-transcription"]');
      if (nonLiveComponent) {
        await nonLiveComponent.processRecording(blob);
      }
    } catch (error) {
      console.error('Error processing recording:', error);
      alert('Failed to process recording. Please try modifying minutes in Presentation Minutes.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getRemainingMinutes = () => {
    const currentPlanLimits = localPlanLimits;
    if (!currentPlanLimits) return 0;

    const minutesLimit = currentPlanLimits.limits.non_live_minutes.limit;
    const minutesUsed = currentPlanLimits.limits.non_live_minutes.used;

    return minutesLimit - minutesUsed;
  };

  // Reset wasVideoUsed when starting a new recording
  const isVideoRecording = useRef(false);

  useEffect(() => {
    if (isRecording && useVideo) {
      isVideoRecording.current = true;
    }
    if (!isRecording) {
      // Only reset when stopping recording
      setTimeout(() => {
        setShowPreview(false);
      }, 0);
    }
  }, [isRecording, useVideo]);

  const savePresentation = async () => {
    try {
      setProcessingState('processing');
      const recordedBlob = new Blob(audioChunksRef.current, { type: getMimeType() });
      setAudioBlob(recordedBlob);

      // If user is not on Student plan, process the recording
      if (user?.plan?.features?.advance_analytics !== false) {
        // Get reference to NonLiveTranscription component
        const nonLiveComponent = document.querySelector('[data-component="non-live-transcription"]');
        if (nonLiveComponent) {
          await nonLiveComponent.processRecording(recordedBlob);
        }
      }

      setProcessingState('saving');
      const data = await authenticatedFetch('/api/presentation/save-transcription', {
        method: 'POST',
        body: {
          // For Student plan, send empty transcript
          transcript: user?.plan?.features?.advance_analytics === false ? '' : transcriptData.fullText,
          segments: user?.plan?.features?.advance_analytics === false ? [] : transcriptData.segments,
          title: title || 'Practice Presentation',
          original_presentation_id: originalPresentationId,
          is_rehearsal: isRehearsal,
          video_analysis: isVideoRecording.current ? videoAnalysis : null,
        }
      });
      console.log("data, ", data, "Data.xp_update: ", data.xp_update);
      // Update user XP if XP update is included in response
      if (data.xp_update) {
        const updatedUser = {
          ...user,
          total_xp: (data.xp_update || 0)
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        handleAuthSuccess(localStorage.getItem('token'), updatedUser);
      }
      navigate(`/presentations/${data.presentation_id}`);
    } catch (error) {
      console.error('Error saving presentation:', error);
      alert('Failed to save presentation. Please try again.');
      setProcessingState('stopped');
    }
  };
  return (
    <div className={`${styles.container} ${isRehearsal ? styles.rehearsal : ''}`}>
      {showPermissionPrompt && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Media Access Required</h2>
            <p className={styles.modalText}>
              This app needs access to your {useVideo ? 'microphone and camera' : 'microphone'} to record your presentation.
              Please enable access in your browser settings to continue.
              {user.plan.features.advance_analytics === false && (
                <><br /><span>*Students can only do video practice. So you need to enable camera</span></>
              )}
            </p>
            <p className={styles.modalPrivacy}>
              Your recording is processed securely and only used for transcription.
            </p>
            <div className={styles.modalInstructions}>
              <ol>
                <li>Click the camera/microphone icon in your browser's address bar</li>
                <li>Select "Allow" for {useVideo ? 'camera and microphone' : 'microphone'} access</li>
                <li>Refresh this page</li>
              </ol>
            </div>
            <div className={styles.modalButtons}>
              <button className={styles.modalSecondaryButton} onClick={() => setShowPermissionPrompt(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`${styles.content} ${isRehearsal ? styles.rehearsal : ''}`}>
        {!isRehearsal ? (
          <div className={styles.header}>
            <h1 className={styles.pageTitle}>Record Your Speech</h1>
          </div>
        ) : (
          <div className={styles.header}>
            <h1 className={styles.pageTitle}>Practice Again</h1>
          </div>
        )}
        <div className={styles.mainWrapper}>
          <div className={styles.mainHeader}>
            <div className={styles.modeSwitch}>
              <button
                className={`${styles.modeButton} ${styles.active}`}
              >
                <FileText size={20} />
                <span className="button-text"> Practice</span>
              </button>
            </div>
            <div className={styles.controlBar}>
              <DeviceSettingsPanel
                selectedAudioDevice={selectedAudioDevice}
                selectedVideoDevice={selectedVideoDevice}
                selectedLanguage={selectedLanguage}
                onAudioDeviceChange={handleAudioDeviceChange}
                onVideoDeviceChange={handleVideoDeviceChange}
                onLanguageChange={handleLanguageChange}
                disabled={isRecording}
                isLiveMode={false}
                onMicToggle={handleAudioDeviceChange}
                onCameraToggle={handleCameraToggle}
              />
            </div>
          </div>
          <div className={styles.mainSection}>
            <div className={`${styles.recordingContainer} ${(!useVideo || !showPreview) ? styles.audioOnly : ''}`}>
              <div className={`${styles.transcriptionWrapper} ${useVideo && showPreview ? styles.withPreview : ''}`}>
                <NonLiveTranscription
                  selectedLanguage={selectedLanguage}
                  selectedAudioDevice={selectedAudioDevice}
                  selectedVideoDevice={selectedVideoDevice}
                  onAudioDeviceChange={handleAudioDeviceChange}
                  onVideoDeviceChange={handleVideoDeviceChange}
                  isRecording={isRecording}
                  mediaRecorderRef={mediaRecorderRef}
                  onTranscriptUpdate={handleTranscriptUpdate}
                  useVideo={useVideo}
                  advanceAnalytics={user?.plan?.features?.advance_analytics || false}
                />
              </div>
              {useVideo && showPreview && (
                <div className={`${styles.videoPreview} `}>
                  <VideoAnalyzer
                    data-component="video-analyzer"
                    videoStream={videoStream}
                    isRecording={isRecording}
                    onAnalysisUpdate={(analysisData) => {
                      if (isRecording && useVideo) {
                        videoAnalysisLogger.logAnalysis(analysisData);
                      }
                    }}
                    transcriptSegments={[]}
                    partialTranscript={''}
                    recordingTime={recordingTime}
                    timeLimit={timeLimit}
                    onStopRecording={stopRecording}
                    onSaveRecording={savePresentation}
                    processingState={processingState}
                  />
                </div>
              )}
            </div>

            {audioBlob && !isRecording && (
              <div className={styles.playbackWrapper}>
                {audioBlob.type.includes('video') ? (
                  <CustomVideoPlayer blob={audioBlob} />
                ) : (
                  <CustomAudioPlayer audioBlob={audioBlob} />
                )}
              </div>
            )}

            <div className={`${styles.controlsWrapper} ${isRecording ? styles.isRecording : ''}`}>
              {user?.plan && (
                <div className={styles.timeSelectorWrapper}>
                  <TimeSelector
                    isRecording={isRecording}
                    planSuggestedDuration={user.plan.limits.suggested_duration}
                    remainingMinutes={getRemainingMinutes()}
                    purchasedMinutes={
                      localPlanLimits?.limits?.non_live_minutes?.purchased?.minutes || 0
                    }
                    purchasedExpiry={
                      localPlanLimits?.limits?.non_live_minutes?.purchased?.expiry
                    }
                    onTimeSelected={handleTimeSelected}
                    isLoading={isLoadingLimits}
                    recordingTime={recordingTime}
                    timeLimit={timeLimit}
                    isTimeLimitReached={isTimeLimitReached}
                  />
                </div>
              )}
              <div className={styles.actionButtons}>
                <button
                  className={`${styles.recordButton} ${isRecording ? styles.recording :
                    processingState === 'stopped' && transcriptData?.fullText ? styles.readyToSave : ''
                    }`}
                  onClick={isRecording ? stopRecording :
                    processingState === 'stopped' && transcriptData?.fullText ? savePresentation :
                      startRecording}
                  disabled={
                    isSaving ||
                    isStarting ||
                    isProcessing ||
                    processingState === 'processing' ||
                    processingState === 'saving'
                  }
                >
                  {isRecording ? 'Stop Recording' :
                    isStarting ? 'Starting...' :
                      isProcessing ? 'Processing...' :
                        processingState === 'processing' ? 'Processing...' :
                          processingState === 'saving' ? 'Saving...' :
                            processingState === 'stopped' && (transcriptData?.fullText || user?.plan?.features?.advance_analytics === false) ? 'Save and Analyze' :
                              'Start Practice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Record;